import { CLOSING_TIMEOUT_MS } from '@store/modal/constants';
import { flexStyle, textStyle } from '@themes/mixins';
import styled, { css } from 'styled-components';

import Button from '../Button';
import Icon from '../Icon';

import { CLOSING_CLASSNAME } from './constants';
import { Modes, NotificationModes, Sizes } from './enums';
import { Props } from './types';

export const Root = styled.div<
  Partial<Props<unknown> & { animated?: boolean }>
>`
  ${({ mode }) =>
    flexStyle(
      mode === Modes.default
        ? { align: 'center', justify: 'center' }
        : { justify: 'flex-end' },
    )};
  position: fixed;
  z-index: 1200;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  overflow-y: auto;
  overflow-x: hidden;

  ${({ animated, mode }) =>
    mode === Modes.rightSlide &&
    animated &&
    css`
      @keyframes fadeIn {
        0% {
          background-color: rgba(0, 0, 0, 0);
        }
        100% {
          background-color: rgba(0, 0, 0, 0.5);
        }
      }
      @keyframes fadeOut {
        0% {
          background-color: rgba(0, 0, 0, 0.5);
        }
        100% {
          background-color: rgba(0, 0, 0, 0);
        }
      }

      animation: fadeIn ${CLOSING_TIMEOUT_MS}ms ease-in;

      &.${CLOSING_CLASSNAME} {
        animation: fadeOut ${CLOSING_TIMEOUT_MS}ms ease-out;
      }
    `}
`;
export const Content = styled.div<
  Partial<Props<unknown> & { animated?: boolean }>
>`
  ${flexStyle({ direction: 'column', justify: 'flex-start', wrap: 'nowrap' })};
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ mode }) => `${mode === Modes.default ? 4 : 0}px`};
  overflow-y: ${({ overflowY }) => overflowY};

  ${({ animated, mode, size }) => {
    switch (mode) {
      case Modes.rightSlide:
        return css`
          width: 550px;
          height: 100vh;
          position: relative;
          will-change: transform;

          ${animated &&
          css`
            /* transform, not right: layout properties re-flow the panel and
               the page behind it on every frame */
            @keyframes openSlideFromRight {
              0% {
                transform: translateX(100%);
              }
              100% {
                transform: translateX(0);
              }
            }
            @keyframes closeSlideToRight {
              0% {
                transform: translateX(0);
              }
              100% {
                transform: translateX(100%);
              }
            }

            animation: openSlideFromRight ${CLOSING_TIMEOUT_MS}ms ease-in;

            &.${CLOSING_CLASSNAME} {
              animation: closeSlideToRight ${CLOSING_TIMEOUT_MS}ms ease-out;
            }
          `}
        `;
      default: // default
        return css`
          position: absolute;
          top: 15%;
          max-height: calc(100% - 20%);

          ${() => {
            switch (size) {
              case Sizes.small:
                return css`
                  width: 500px;
                `;
              case Sizes.large:
                return css`
                  width: 80%;
                `;
              case Sizes.fullScreen:
                return css`
                  top: auto;
                  max-height: unset;
                  width: 97%;
                  height: 97%;
                `;
              default: // medium
                return css`
                  width: 50%;
                `;
            }
          }}
        `;
    }
  }}

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.grey};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background-color: ${({ theme }) => theme.colors.secondaryBackground};
  }
`;
export const Header = styled.div<Partial<Props<unknown>>>`
  ${flexStyle({
    align: 'flex-start',
    justify: 'space-between',
    wrap: 'nowrap',
  })};

  ${({ stickyHeader }) =>
    stickyHeader &&
    css`
      position: sticky;
      top: 0;
      z-index: 2;
    `};
  ${({ boardedHeader }) =>
    boardedHeader &&
    css`
      border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
    `};

  background-color: ${({ theme }) => theme.colors.background};
  padding: 32px 32px 30px;
`;
export const Body = styled.div`
  ${flexStyle({ direction: 'column', justify: 'flex-start', wrap: 'nowrap' })};
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 0 32px 32px;
  flex: 1;
  z-index: 1;
  max-height: 100%;
`;
export const Title = styled.h1`
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1.2,
    size: '18px',
    textAlign: 'left',
    weight: 500,
  })};
  letter-spacing: 0.15px;
  max-width: calc(100% - 20px);
  word-wrap: break-word;
  margin: 0;
`;
export const ActionsContainer = styled.div`
  ${flexStyle({ align: 'center', justify: 'flex-start', wrap: 'nowrap' })};
  margin-left: auto;
`;
export const CloseIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
`;
export const ConfirmContainer = styled.div`
  ${flexStyle({ direction: 'column', justify: 'flex-start', wrap: 'nowrap' })};
`;
export const ConfirmText = styled.span`
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1.4,
    size: '14px',
    textAlign: 'left',
    weight: 400,
  })};
  letter-spacing: 0.25px;
  margin-bottom: 50px;
`;
export const ConfirmButtonsContainer = styled.div`
  ${flexStyle({ justify: 'space-between', wrap: 'nowrap' })};
  gap: 16px;

  & > * {
    flex: 1;
  }
`;
export const ConfirmButton = styled(Button)`
  /* flex: 1 is now handled by parent container */
`;
export const NotificationContainer = styled.div`
  ${flexStyle({ direction: 'column', justify: 'flex-start', wrap: 'nowrap' })};
`;
export const NotificationIconContainer = styled.div`
  ${flexStyle({
    direction: 'column',
    justify: 'flex-start',
    align: 'center',
    wrap: 'nowrap',
  })};
`;
export const NotificationIcon = styled(Icon)<{
  notificationMode?: NotificationModes;
}>`
  color: ${({ theme }) => theme.colors.textTertiary};
  margin-bottom: 15px;

  ${({ notificationMode }) => {
    switch (notificationMode) {
      case NotificationModes.success:
        return css`
          color: ${({ theme }) => theme.colors.green};
        `;
      case NotificationModes.error:
        return css`
          color: ${({ theme }) => theme.colors.red};
        `;
      default:
        return css`
          color: ${({ theme }) => theme.colors.yellow};
        `;
    }
  }}
`;
export const NotificationLabel = styled.div`
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1,
    size: '22px',
    textAlign: 'left',
    weight: 500,
  })};
  margin-bottom: 30px;
`;
export const NotificationText = styled.span`
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1.4,
    size: '14px',
    textAlign: 'left',
    weight: 400,
  })};
  letter-spacing: 0.25px;
  white-space: pre-line;
`;
